import { useEffect, useRef } from 'react'
import { amplitudeEvent } from 'lib/amplitude'
import { useAuth } from 'lib/auth'
import { isMarketplaceCandidate } from 'lib/coach-profile/marketplace-listing'

export const MARKETPLACE_LISTING_ACTIONS = {
  GO_TO_ADM_TRAINING: 'go-to-adm-training',
  TURN_ON_VISIBILITY: 'turn-on-visibility',
  MANAGE_FACILITIES: 'manage-facilities',
  TURN_ON_CONTACT: 'turn-on-contact',
  ADD_OFFERING: 'add-offering',
  USE_OWN_BOOKING_TOOL: 'use-own-booking-tool',
  MANAGE_BOOKING_TYPES: 'manage-booking-types',
  UPGRADE: 'upgrade',
}

const listingProperties = ({ reasons, dashboardVariant, hasPaidTier }) => ({
  reasons,
  reason_count: reasons.length,
  dashboard_variant: dashboardVariant,
  has_paid_tier: Boolean(hasPaidTier),
  path: window.location.pathname,
})

export const trackMarketplaceListingStatusChecked = ({
  reasons,
  dashboardVariant,
  hasPaidTier,
}) => {
  amplitudeEvent('marketplace-listing-status-checked', {
    is_listable: reasons.length === 0,
    ...listingProperties({ reasons, dashboardVariant, hasPaidTier }),
  })
}

export const trackMarketplaceListingBannerViewed = ({
  reasons,
  dashboardVariant,
  hasPaidTier,
}) => {
  amplitudeEvent(
    'marketplace-listing-banner-viewed',
    listingProperties({ reasons, dashboardVariant, hasPaidTier }),
  )
}

export const trackMarketplaceListingBannerActionClicked = ({
  reason,
  action,
  destination,
}) => {
  amplitudeEvent('marketplace-listing-banner-action-clicked', {
    reason,
    action,
    destination: destination ?? null,
    path: window.location.pathname,
  })
}

// Admins in Assume User mode see the coach's dashboard, but that isn't the
// coach seeing the banner, so nothing is tracked for assumed sessions.
export const useIsMarketplaceTrackingAllowed = () => {
  const { user } = useAuth()
  return !user?.isAssumed
}

// Fires once per dashboard mount so switching between the onboarding and
// default dashboard (or refetching the coach) doesn't double count.
export const useMarketplaceListingTracking = ({
  enabled,
  coach,
  reasons,
  dashboardVariant,
}) => {
  const { user } = useAuth()
  const isTrackingAllowed = useIsMarketplaceTrackingAllowed()
  const hasTracked = useRef(false)
  const shouldTrack =
    enabled && isTrackingAllowed && isMarketplaceCandidate(coach)
  const hasPaidTier = user?.coach?.hasPaidTierSubscription

  useEffect(() => {
    if (!shouldTrack || hasTracked.current) return
    hasTracked.current = true

    const properties = { reasons, dashboardVariant, hasPaidTier }
    trackMarketplaceListingStatusChecked(properties)
    if (reasons.length > 0) {
      trackMarketplaceListingBannerViewed(properties)
    }
  }, [shouldTrack, reasons, dashboardVariant, hasPaidTier])
}
